import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CONTACT } from "@/constants/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  vehicle: "",
  departure: "",
  arrival: "",
  date: "",
  message: "",
};

export const ConvoyageForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Consentement requis", {
        description: "Veuillez accepter l'utilisation de vos données pour traiter votre demande.",
      });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/convoyage`, { ...form, consent });
      const waText = [
        "Bonjour MECA AUTO CLEAN 01, je souhaite un convoyage de véhicule.",
        `Nom : ${form.name}`,
        `Téléphone : ${form.phone}`,
        `E-mail : ${form.email}`,
        `Véhicule : ${form.vehicle}`,
        `Départ : ${form.departure}`,
        `Arrivée : ${form.arrival}`,
        form.date ? `Date souhaitée : ${form.date}` : null,
        form.message ? `Message : ${form.message}` : null,
      ]
        .filter(Boolean)
        .join("\n");
      window.open(
        `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(waText)}`,
        "_blank",
      );
      toast.success("Demande envoyée", {
        description:
          "WhatsApp s'ouvre pour transmettre votre demande au garage. Une copie est aussi envoyée par e-mail.",
      });
      setForm(emptyForm);
      setConsent(false);
    } catch {
      toast.error("Envoi impossible", {
        description: `Réessayez dans un instant ou appelez le garage au ${CONTACT.phoneDisplay}.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="convoyage-form" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          required
          placeholder="Votre nom *"
          value={form.name}
          onChange={set("name")}
          data-testid="convoyage-name-input"
          aria-label="Votre nom"
        />
        <Input
          required
          type="tel"
          placeholder="Votre téléphone *"
          value={form.phone}
          onChange={set("phone")}
          data-testid="convoyage-phone-input"
          aria-label="Votre téléphone"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          required
          type="email"
          placeholder="Votre e-mail *"
          value={form.email}
          onChange={set("email")}
          data-testid="convoyage-email-input"
          aria-label="Votre e-mail"
        />
        <Input
          required
          placeholder="Véhicule à convoyer *"
          value={form.vehicle}
          onChange={set("vehicle")}
          data-testid="convoyage-vehicle-input"
          aria-label="Véhicule à convoyer"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          required
          placeholder="Lieu de départ *"
          value={form.departure}
          onChange={set("departure")}
          data-testid="convoyage-departure-input"
          aria-label="Lieu de départ"
        />
        <Input
          required
          placeholder="Lieu d'arrivée *"
          value={form.arrival}
          onChange={set("arrival")}
          data-testid="convoyage-arrival-input"
          aria-label="Lieu d'arrivée"
        />
      </div>
      <Input
        placeholder="Date souhaitée"
        value={form.date}
        onChange={set("date")}
        data-testid="convoyage-date-input"
        aria-label="Date souhaitée"
      />
      <Textarea
        rows={3}
        placeholder="Précisions (état du véhicule, contraintes horaires...)"
        value={form.message}
        onChange={set("message")}
        data-testid="convoyage-message-input"
        aria-label="Votre message"
      />
      <label className="flex items-start gap-3 text-sm text-muted-foreground">
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => setConsent(v === true)}
          data-testid="convoyage-consent-checkbox"
          aria-label="Consentement au traitement des données"
          className="mt-0.5"
        />
        <span>
          J'accepte que mes données soient utilisées pour traiter ma demande,
          conformément à la{" "}
          <Link
            to="/politique-de-confidentialite"
            data-testid="convoyage-privacy-link"
            className="text-primary underline-offset-4 hover:underline"
          >
            politique de confidentialité
          </Link>
          . *
        </span>
      </label>
      <Button type="submit" disabled={sending} data-testid="convoyage-submit-button" className="w-full sm:w-auto">
        {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Envoyer ma demande
      </Button>
    </form>
  );
};
